import type { LinkedInCompanyData } from "@/api/gen-api";
import { cn } from "@/lib/utils";
import {
  Building2,
  Calendar,
  ExternalLink,
  TrendingUp,
  Users,
} from "lucide-react";

/**
 * The body of a LinkedIn company scrape, as the deal page renders it.
 *
 * Lifted out of `deal-detail/data-lake/linkedin-info.tsx` so the public lookbook
 * can show the same report. That component owns the *page*: fetching, versions,
 * the scrape button, deletion — all of which reach for the workspace store, the
 * current-deal store and `aiApi`. This owns only the presentation, imports none
 * of that (the `LinkedInCompanyData` import is type-only, so nothing from the
 * authenticated API layer survives the build), and takes the scrape as a prop.
 *
 * It is also the reason the public page stopped printing markup: the generic
 * structural renderer walked every stored key, so a reader got `Text Html`,
 * `Unformatted About` and `Input` alongside the report. This shows the curated
 * set of fields and nothing else.
 */

/**
 * A stored scrape. Every field is optional because this renders rows that came
 * out of Mongo — older documents predate fields the current worker writes, and
 * a missing `funding` should drop a card rather than throw.
 */
export type LinkedInCompanyReportData = Partial<LinkedInCompanyData>;

/**
 * LinkedIn's CDN is a third party, and on the public lookbook the address bar is
 * a private share link — it has no business being sent in a Referer header.
 * Harmless internally, so it is set in one place for both callers.
 */
const IMG_PROPS = {
  referrerPolicy: "no-referrer",
  loading: "lazy",
} as const;

/** Hides an avatar whose CDN url has expired instead of leaving a broken icon. */
const hideOnError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  (event.target as HTMLImageElement).style.display = "none";
};

function formatDate(dateString?: string) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

const withProtocol = (url: string) =>
  url.startsWith("http") ? url : `https://${url}`;

export function LinkedInCompanyReport({
  data,
  className,
}: {
  data: LinkedInCompanyReportData;
  className?: string;
}) {
  const followers = data.followers ?? 0;
  const employeeCount = data.employees_in_linkedin ?? 0;
  const employees = data.employees ?? [];
  const similar = data.similar ?? [];
  const locations = data.locations ?? [];
  const updates = data.updates ?? [];

  return (
    <div className={cn("@container space-y-6 overflow-hidden", className)}>
      {/* Company header */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 sm:p-5">
        <div className="flex items-start gap-4">
          {data.logo && (
            <img
              {...IMG_PROPS}
              src={data.logo}
              alt={`${data.name ?? "Company"} logo`}
              className="h-14 w-14 shrink-0 rounded-md border border-border bg-background object-cover"
              onError={hideOnError}
            />
          )}
          <div className="min-w-0 flex-1">
            {data.name && (
              <h1 className="break-words text-base font-semibold text-foreground sm:text-lg">
                {data.name}
              </h1>
            )}
            {data.slogan && (
              <p className="mt-0.5 break-words text-sm text-muted-foreground">
                {data.slogan}
              </p>
            )}
            {data.about && (
              <p className="mt-2 whitespace-pre-line break-words text-sm leading-relaxed text-muted-foreground">
                {data.about}
              </p>
            )}
          </div>
        </div>

        {/* Key metrics */}
        <div className="mt-4 grid grid-cols-2 gap-2 @2xl:grid-cols-4 @2xl:gap-3">
          {followers > 0 && (
            <div className="rounded-lg border border-border bg-card p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <TrendingUp className="h-3.5 w-3.5 shrink-0" />
                Followers
              </div>
              <div className="truncate font-mono text-lg font-semibold tabular-nums text-foreground">
                {followers.toLocaleString()}
              </div>
            </div>
          )}
          {employeeCount > 0 && (
            <div className="rounded-lg border border-border bg-card p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5 shrink-0" />
                Employees
              </div>
              <div className="truncate font-mono text-lg font-semibold tabular-nums text-foreground">
                {employeeCount}
              </div>
            </div>
          )}
          {data.founded && (
            <div className="rounded-lg border border-border bg-card p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                Founded
              </div>
              <div className="truncate font-mono text-lg font-semibold tabular-nums text-foreground">
                {data.founded}
              </div>
            </div>
          )}
          {data.company_size && (
            <div className="rounded-lg border border-border bg-card p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Building2 className="h-3.5 w-3.5 shrink-0" />
                Size
              </div>
              <div className="break-words text-sm font-semibold text-foreground">
                {data.company_size}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Company details */}
      <div className="grid gap-4 @lg:grid-cols-2">
        {/* Left column */}
        <div className="min-w-0 space-y-4">
          {data.industries && (
            <Field label="Industries">
              <p className="break-words text-sm text-foreground">
                {data.industries}
              </p>
            </Field>
          )}

          {data.specialties && (
            <Field label="Specialties">
              <p className="break-words text-sm text-foreground">
                {data.specialties}
              </p>
            </Field>
          )}

          {data.headquarters && (
            <Field label="Headquarters">
              <p className="break-words text-sm text-foreground">
                {data.headquarters}
              </p>
            </Field>
          )}

          {locations.length > 0 && (
            <Field label="Locations">
              <ul className="space-y-1.5">
                {locations.map((location, idx) => (
                  <li key={idx} className="break-words text-sm text-foreground">
                    {location}
                  </li>
                ))}
              </ul>
            </Field>
          )}

          {data.website && (
            <Field label="Website">
              <a
                href={withProtocol(data.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{data.website}</span>
              </a>
            </Field>
          )}

          {data.organization_type && (
            <Field label="Organization Type">
              <p className="break-words text-sm text-foreground">
                {data.organization_type}
              </p>
            </Field>
          )}
        </div>

        {/* Right column */}
        <div className="min-w-0 space-y-4">
          {employees.length > 0 && (
            <Field label={`Related People (${employees.length})`}>
              <div className="space-y-1">
                {employees.slice(0, 10).map((employee, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-muted/50"
                  >
                    {employee.img && (
                      <img
                        {...IMG_PROPS}
                        src={employee.img}
                        alt={employee.title}
                        className="h-9 w-9 shrink-0 rounded-full border border-border object-cover"
                        onError={hideOnError}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      {employee.link ? (
                        <a
                          href={employee.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary"
                        >
                          <span className="truncate">{employee.title}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      ) : (
                        <p className="truncate text-sm font-medium text-foreground">
                          {employee.title}
                        </p>
                      )}
                      {employee.subtitle && (
                        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {employee.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Field>
          )}

          {data.funding && (
            <Field label="Funding">
              <dl className="space-y-1.5 text-sm">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-muted-foreground">Last round</dt>
                  <dd className="break-words text-foreground">
                    {data.funding.last_round_type} ·{" "}
                    {formatDate(data.funding.last_round_date)}
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-muted-foreground">
                    Total rounds
                  </dt>
                  <dd className="font-mono tabular-nums text-foreground">
                    {data.funding.rounds}
                  </dd>
                </div>
              </dl>
            </Field>
          )}

          {similar.length > 0 && (
            <Field label="Similar Companies">
              <div className="space-y-1">
                {similar.slice(0, 5).map((company, idx) => (
                  <a
                    key={idx}
                    href={company.Links}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-md p-2 transition-colors hover:bg-muted/50"
                  >
                    <p className="break-words text-sm font-medium text-foreground">
                      {company.title}
                    </p>
                    {company.subtitle && (
                      <p className="break-words text-xs text-muted-foreground">
                        {company.subtitle}
                      </p>
                    )}
                  </a>
                ))}
              </div>
            </Field>
          )}
        </div>
      </div>

      {/* Recent updates */}
      {updates.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4 sm:p-5">
          <h3 className="mb-3 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/60">
            Recent Updates
          </h3>
          <div className="space-y-4">
            {updates.slice(0, 5).map((update, idx) => (
              <div
                key={idx}
                className="border-b border-border pb-4 last:border-0 last:pb-0"
              >
                <div className="mb-1.5 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium text-foreground">
                      {update.title}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {update.time} · {update.likes_count} likes
                      {update.comments_count
                        ? ` · ${update.comments_count} comments`
                        : ""}
                    </p>
                  </div>
                  {update.post_url && (
                    <a
                      href={update.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                {/* `text`, never `text_html`: the scrape carries both, and the
                    HTML one is LinkedIn's own markup, which belongs in a post,
                    not in a report. */}
                {update.text && (
                  <p className="mb-3 line-clamp-3 break-words text-sm text-muted-foreground">
                    {update.text}
                  </p>
                )}
                {update.images && update.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {update.images.slice(0, 2).map((img, imgIdx) => (
                      <img
                        {...IMG_PROPS}
                        key={imgIdx}
                        src={img}
                        alt={`Update ${idx + 1} image ${imgIdx + 1}`}
                        className="h-24 w-full rounded-md border border-border object-cover sm:h-32"
                        onError={hideOnError}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-2 text-[11px] font-medium uppercase tracking-widest text-muted-foreground/60">
        {label}
      </h3>
      {children}
    </div>
  );
}
